import api, { ApiResponse, QueryPayload, QueryResponse, WhereCondition } from './api';
import type { UserSettings } from '../utils/user-settings';

export interface User {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  role: 'admin' | 'media_buyer' | 'viewer' | 'partner';
  status: 'active' | 'inactive' | 'pending';
  avatar_url: string | null;
  partner_id: string | null;
  partner_name: string | null;
  settings: UserSettings;
  workspace_ids?: string[];
  birthday?: string | null;
  last_login_at: string | null;
  created_at: string;
}

export interface UserFilters {
  search?: string;
  role?: string;
  status?: string;
  offset?: number;
  limit?: number;
}

export interface CreateUserInput {
  email: string;
  first_name?: string;
  last_name?: string;
  role: User['role'];
  password?: string;
  partner_id?: string | null;
  workspaceIds?: string[];
}

export interface UpdateUserInput {
  first_name?: string;
  last_name?: string;
  role?: User['role'];
  status?: User['status'];
  avatar_url?: string;
  settings?: UserSettings;
  workspaceIds?: string[];
  birthday?: string | null;
}


export interface QueryResult<T> {
  data: T[];
  total: number;
}

function buildWhereCondition(filters: UserFilters): WhereCondition | undefined {
  const conditions: WhereCondition[] = [];

  if (filters.role) {
    conditions.push({ user: { role: filters.role } });
  }

  if (filters.status) {
    conditions.push({ user: { status: filters.status } });
  }

  if (filters.search) {
    conditions.push({
      $or: [
        { user: { email: { op: 'ilike', value: `%${filters.search}%` } } },
        { user: { first_name: { op: 'ilike', value: `%${filters.search}%` } } },
        { user: { last_name: { op: 'ilike', value: `%${filters.search}%` } } },
      ],
    });
  }

  return conditions.length > 0 ? { $and: conditions } : undefined;
}

export const usersService = {
  query: async (filters: UserFilters = {}): Promise<QueryResult<User>> => {
    const payload: QueryPayload = {
      where: buildWhereCondition(filters),
      offset: filters.offset ?? 0,
      limit: filters.limit ?? 25,
      order_by: 'user.created_at',
      order: 'desc',
    };

    const response = await api.post<QueryResponse<User>>('/users/query', payload);
    return {
      data: response.data.data,
      total: response.data._meta?.total ?? 0,
    };
  },

  getById: async (id: string): Promise<User> => {
    const response = await api.get<ApiResponse<User>>(`/users/${id}`);
    return response.data.data!;
  },

  create: async (data: CreateUserInput): Promise<User> => {
    const response = await api.post<ApiResponse<User>>('/users', data);
    return response.data.data!;
  },

  update: async (id: string, data: UpdateUserInput): Promise<User> => {
    const response = await api.patch<ApiResponse<User>>(`/users/${id}`, data);
    return response.data.data!;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/users/${id}`);
  },
};
